import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { euro, formatDate } from "@/lib/constants";
import { Plus, Search, MapPin } from "lucide-react";

export default function TripsList() {
  const [trips, setTrips] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tutte");
  const navigate = useNavigate();
  const { user, activeCompany } = useAuth();
  const effectiveCompany = activeCompany || user?.active_company || user?.company;

  const loadTrips = useCallback(() => api.get("/trips").then((r) => setTrips(r.data)), [effectiveCompany]);
  useEffect(() => { loadTrips(); }, [loadTrips]);

  const today = new Date().toISOString().slice(0, 10);
  let list = trips;
  if (filter === "future") list = trips.filter((t) => (t.data_partenza || "") >= today && t.stato !== "annullata");
  else if (filter === "recenti") list = [...trips].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  else if (filter !== "tutte") list = trips.filter((t) => t.stato === filter);
  if (q) list = list.filter((t) => `${t.destinazione} ${t.dipendente} ${t.cliente_sede}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-display text-3xl font-bold text-slate-900">Le mie trasferte</h1>
        <Button data-testid="btn-new-trip-list" onClick={() => navigate("/nuova-trasferta")} className="w-full justify-center bg-[#E30613] font-semibold hover:bg-[#C50510] sm:w-auto">
          <Plus size={18} className="mr-1.5" /> Nuova trasferta
        </Button>
      </div>

      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="max-w-full overflow-x-auto">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList data-testid="trips-filter" className="w-max min-w-max">
              <TabsTrigger value="tutte" data-testid="filter-all" className="shrink-0 px-2 sm:px-3">Tutte</TabsTrigger>
              <TabsTrigger value="future" data-testid="filter-future" className="shrink-0 px-2 sm:px-3">Future</TabsTrigger>
              <TabsTrigger value="recenti" data-testid="filter-recent" className="shrink-0 px-2 sm:px-3">Recenti</TabsTrigger>
              <TabsTrigger value="in_approvazione" data-testid="filter-approval" className="shrink-0 px-2 sm:px-3">Da approvare</TabsTrigger>
              <TabsTrigger value="bozza" data-testid="filter-draft" className="shrink-0 px-2 sm:px-3">Bozze</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="trips-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="pl-9" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wider">
          <div className="col-span-3">Destinazione</div>
          <div className="col-span-2">Dipendente</div>
          <div className="col-span-2">Partenza</div>
          <div className="col-span-2">Rientro</div>
          <div className="col-span-1 text-right">Costo</div>
          <div className="col-span-2 text-right">Stato</div>
        </div>
        {list.length === 0 && <div className="px-5 py-10 text-center text-slate-400">Nessuna trasferta trovata.</div>}
        {list.map((t) => (
          <button
            key={t.id}
            data-testid={`trip-item-${t.id}`}
            onClick={() => navigate(`/trasferte/${t.id}`)}
            className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-slate-50 hover:bg-slate-50 transition-colors items-center"
          >
            <div className="col-span-3 font-medium text-slate-900 flex items-center gap-2">
              <MapPin size={15} className="text-[#E30613]" /> {t.destinazione || "—"}
              {t.cliente_sede && <span className="text-xs text-slate-400 hidden lg:inline">· {t.cliente_sede}</span>}
            </div>
            <div className="col-span-2 text-sm text-slate-600">{t.dipendente}</div>
            <div className="col-span-2 text-sm text-slate-600">{formatDate(t.data_partenza)}</div>
            <div className="col-span-2 text-sm text-slate-600">{formatDate(t.data_rientro)}</div>
            <div className="col-span-1 text-sm text-slate-700 md:text-right font-medium">{euro(t.preventivo?.totale)}</div>
            <div className="col-span-2 md:text-right"><StatusBadge stato={t.stato} /></div>
          </button>
        ))}
      </div>
    </Layout>
  );
}
